import React from 'react'
import { css } from '@emotion/react'

import CloseIcon from '../icons/CloseIcon'
import Colors from '../theme/Colors'
import { Breakpoints } from '../theme/Responsive'
import { enlargeButtonLike, mapChildren } from './Button'
import ClickArea from './ClickArea'
import ClickOutsideLayer from './ClickOutsideLayer'
import Keyboard from './Keyboard'
import Popup from './Popup'
import Text from './Text'

const dimension = '480px'

export default function Modal(props: {
  compact?: boolean
  title?: React.ReactNode
  children: React.ReactNode
  buttons?: React.ReactNode
  className?: string
  footer?: React.ReactNode
  onSubmit?: (e: React.FormEvent) => void
  onClose?: () => void
  ['data-testid']?: string
}) {
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && props.onClose) {
      e.stopPropagation()

      props.onClose()
    }
  }

  const renderCloseButton = () => {
    if (!props.onClose) {
      return null
    }

    return (
      <ClickArea
        css={css`
          position: absolute;
          top: 0;
          right: 0;
          padding: 15px;
          font-size: 18px;
          outline: none;
          color: ${Colors.Gray300};

          &:hover,
          &:focus,
          &:active {
            color: ${Colors.Gray500};
          }
        `}
        aria-label="close"
        data-testid="modal__close-button"
        onClick={props.onClose}
      >
        <CloseIcon
          css={css`
            display: block;
          `}
        />
      </ClickArea>
    )
  }

  const renderButtons = () => {
    if (!props.buttons) {
      return null
    }

    return (
      <div
        css={css`
          margin-top: ${props.compact ? '25px' : '30px'};
          display: flex;
          flex-direction: ${props.compact ? 'row' : 'column'};
          justify-content: ${props.compact ? 'flex-end' : 'center'};
          align-items: center;

          @media (max-width: ${Breakpoints.Mobile}px) {
            margin-top: 20px;
            flex-direction: column;
          }

          > * {
            margin: ${props.compact ? '0 10px 0 0' : '0 0 10px 0'};
            &:last-child {
              margin: 0;
            }

            @media (max-width: ${Breakpoints.Mobile}px) {
              margin: 0 0 10px 0;
              width: 100%;
            }
          }
        `}
      >
        {mapChildren(props.buttons, (element) =>
          enlargeButtonLike(element, props.compact ? undefined : 'large'),
        )}
      </div>
    )
  }

  const renderTitle = () => {
    if (!props.title) {
      return null
    }

    return (
      <div
        css={css`
          margin-bottom: ${props.compact ? '10px' : '20px'};
          text-align: ${props.compact ? 'left' : 'center'};
        `}
      >
        <Text size={24} bold>
          {props.title}
        </Text>
      </div>
    )
  }

  const renderContent = () => {
    const content = (
      <React.Fragment>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;

            & > * {
              width: 100%;
            }

            @media (max-width: ${Breakpoints.Mobile}px) {
              flex: 1;
            }
          `}
        >
          {props.children}
        </div>

        {renderButtons()}
      </React.Fragment>
    )

    if (props.onSubmit) {
      return <form onSubmit={props.onSubmit}>{content}</form>
    }

    return content
  }

  return (
    <Popup>
      <Keyboard onKeyDown={onKeyDown}>
        <div
          css={css`
            position: fixed;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            display: flex;
            align-items: center;
            justify-content: center;
          `}
        >
          <ClickOutsideLayer dim onClickOutside={props.onClose} />

          <div
            className={props.className}
            css={css`
              position: relative; /* For the close button */
              border-radius: 5px;
              background: white;
              width: ${dimension};
              max-height: calc(100% - 40px);
              overflow-x: hidden;
              overflow-y: auto;
              box-sizing: border-box;
              display: flex;
              flex-direction: column;

              ::-webkit-scrollbar-track,
              ::-webkit-scrollbar {
                border-top-right-radius: 2px;
                border-bottom-right-radius: 2px;
              }

              @media (max-width: ${Breakpoints.Mobile}px) {
                border-radius: 0;
                width: 100%;
                min-width: auto;
                max-width: none;
                height: 100%;
                max-height: none;
              }
            `}
            data-testid={props['data-testid']}
          >
            <div
              css={css`
                padding: ${props.compact ? '35px 25px 25px 30px' : '50px 60px'};
                @media (max-width: ${Breakpoints.Mobile}px) {
                  padding: 40px 20px 20px;
                }
              `}
            >
              {renderCloseButton()}
              {renderTitle()}
              {renderContent()}
            </div>

            <div
              css={css`
                position: sticky;
                bottom: 0;
                left: 0;
                right: 0;
              `}
            >
              {props.footer}
            </div>
          </div>
        </div>
      </Keyboard>
    </Popup>
  )
}
